import { tt } from '@agos/shared'

/**
 * Who is serving this table — `FRONTEND.md` §9.40, ported from legacy `new-order.tsx:341`.
 *
 * `buildOrderPayload` has carried `employeeId` since it was written and **nothing ever set it**, so
 * every table order AGOS took was written with no waiter against it. That is the person who owns the
 * money: the backend's `SalesChannel.requiresWaiter()` exists to ask for it, and the field it reads
 * was always null.
 */

export interface WaiterOption {
  id: number
  name: string
}

/** `GET /employees/waiters` — whoever is **on shift**, which is not the same as whoever is employed. */
export function waiterOptions(payload: unknown): WaiterOption[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as { id?: unknown; name?: unknown }
    if (typeof row.id !== 'number') return []
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    return [{ id: row.id, name: name === '' ? `#${row.id}` : name }]
  })
}

/**
 * What the screen knows about the shift list - `FRONTEND.md` §9.633, FB-948.
 *
 * **A fourth state, because the read can fail and that is not the same as waiting.** This screen gave up
 * `isSuccess` alone, so a failed read answered *still loading* for ever while the dropdown's own placeholder -
 * keyed on the array rather than on the flag - said *nobody on shift*. **Two claims about one read, neither
 * true, with the save blocked by the first and the second telling the operator what to do about it.**
 *
 * Exhaustive in both readers below, so a fifth is a compile error rather than a field with nothing to say
 * (§9.507's guarantee).
 */
export type WaiterListState = 'ready' | 'loading' | 'unreadable' | 'nobody'

export function waiterListState(
  loaded: boolean,
  failed: boolean,
  waiters: readonly WaiterOption[],
): WaiterListState {
  // The failure first: on a failed read `loaded` is false **and** the list is empty, so either of the two
  // below would answer before it and say something that is not so.
  if (failed) return 'unreadable'
  if (!loaded) return 'loading'
  return waiters.length === 0 ? 'nobody' : 'ready'
}

/**
 * What the dropdown's own placeholder says.
 *
 * **It was `waiters.length === 0 ? 'Nikto na smene' : '- vyberte -'`**, which is the claim this entry is about:
 * an empty array is what a failed read and an empty shift both leave behind, and only one of them is somebody's
 * to fix. Derived from the same state as the refusal below, so the two cannot drift apart again.
 */
export function waiterPlaceholder(state: WaiterListState): string {
  switch (state) {
    case 'ready':
      return tt('- vyberte -')
    case 'loading':
      return tt('Načítava sa...')
    case 'unreadable':
      return tt('Zoznam sa nepodarilo načítať')
    case 'nobody':
      return tt('Nikto na smene')
  }
}

/**
 * Why this order cannot be saved yet, or `null`.
 *
 * Legacy's three refusals, kept apart because they are three different things to do about it, and §9.633's
 * fourth:
 *
 * - **still loading** — wait, the answer is on its way;
 * - **unreadable** - the read failed, which is neither of the two below and is the one an operator would
 *   otherwise chase in the wrong place;
 * - **nobody on shift** — somebody has to clock in before a table can be served, which is §9.32's
 *   widget and not something this screen can fix;
 * - **none chosen** — the list is there, pick from it.
 *
 * Collapsing them into *"vyberte čašníka"* would tell a waitress to choose from an empty list.
 */
export function waiterProblem(
  needsWaiter: boolean,
  state: WaiterListState,
  chosenId: number | null,
): string | null {
  if (!needsWaiter) return null
  switch (state) {
    case 'loading':
      return tt('Zoznam čašníkov sa ešte načítava.')
    case 'unreadable':
      return tt(
        'Zoznam čašníkov sa nepodarilo načítať. Neznamená to, že nikto nie je na smene - skúste to znova.',
      )
    case 'nobody':
      return tt('Žiadny čašník nie je nahlásený na smene. Nahláste aspoň jedného čašníka.')
    case 'ready':
      return chosenId === null ? tt('Pred uložením objednávky vyberte čašníka.') : null
  }
}

/**
 * The waiter to start with: yourself, when you are one.
 *
 * The second half of the owner's request — *automaticky selektnut toho, kto objednáva*. A manager
 * taking an order for somebody else's table is offered nothing, because the app cannot know whose
 * table it is and guessing would put the takings against the wrong person.
 *
 * Only when they are actually on the shift list: a waiter who has not clocked in is not on it, and
 * pre-selecting an id the `<select>` has no option for would show an empty control that looks chosen.
 */
export function initialWaiterId(
  user: { id?: number; systemRole?: string } | null | undefined,
  waiters: readonly WaiterOption[],
): number | null {
  if (user?.systemRole !== 'waiter' || typeof user.id !== 'number') return null
  return waiters.some((waiter) => waiter.id === user.id) ? user.id : null
}

/**
 * The person closing, as the employee the close names - `FRONTEND.md` §9.740, owner O111.
 *
 * <p>The owner, of *Vydaná* on *Rozvoz - nástenka*: *"assign it to someone, who use «Vydaná» button"*. The board's
 * *Zaplatiť* therefore names the signed-in employee, and the server's own rule for a named closer applies
 * (`BACKEND.md` §10A.386, `HandoverOwner.named`): that person owns the order, unless a rozvozár carried it - took it
 * out on the road or was paid on his own terminal - or it was handed over before. Naming nobody would leave an order
 * a colleague rang up with her, which is the till's question (owner question #152) and not what was asked here.
 *
 * <p><b>Nobody for a Control Tower session</b>: its id belongs to the platform, not to this franchise's employees, and
 * the server refuses an id that names nobody here - so it names nobody and the server's session rule decides, which
 * never takes a Tower token for an employee either.
 */
export function closerEmployeeId(
  user: { id?: number; isSuperAdmin?: boolean } | null | undefined,
): number | null {
  if (user === null || user === undefined || user.isSuperAdmin === true) return null
  return typeof user.id === 'number' && user.id > 0 ? user.id : null
}

/**
 * The same question at the till — `FRONTEND.md` §9.128, the owner's point 13.
 *
 * Separate from `waiterProblem` only for its wording: the order is already saved by the time
 * somebody is paying, so *"pred uložením objednávky"* would name the wrong moment. The rules are the
 * same rules, and they are the reason legacy refuses the payment rather than defaulting: crediting
 * a colleague's takings to whoever happens to be at the screen is the very error being reported.
 *
 * <p>§9.736 preselects the signed-in waitress where the till asks ({@link initialPayWaiterId}). That is an
 * answer drawn in the select, which the person settling sees and changes at a shared till - not a default the
 * payload adds behind the question - so this refusal still stands wherever nobody is preselected.
 */
export function payWaiterProblem(
  needsWaiter: boolean,
  loaded: boolean,
  waiters: readonly WaiterOption[],
  chosenId: number | null,
): string | null {
  if (!needsWaiter) return null
  if (!loaded) return tt('Zoznam čašníkov sa ešte načítava.')
  if (waiters.length === 0) {
    return tt('Žiadni čašníci s dochádzkou - pridajte dochádzku pred platbou.')
  }
  if (chosenId === null) return tt('Platbu nie je možné spracovať bez výberu čašníka.')
  return null
}

/**
 * Whether this kind of order is attributed to a waiter at all - §9.333.
 *
 * A delivery belongs to its driver, so it is never a waiter's.
 *
 * **This read `orderType === 'restaurant' || orderType === 'bar'`, and that missed `na_sekeru`** -
 * the owner's own channel, and the one they reported stuck: *"when using na _sekeru and its not
 * assigned to anyone, its in zoznam & archív as kontrola kvality, but we cant move it."* One of the
 * hardcoded channel lists §10A.1 exists to remove.
 *
 * **The server's rule is `usesTables`**, and `OrderStatusService.guardClosable` refuses to close an
 * order on such a channel without a waiter. So that is the rule here too, read off the channel.
 *
 * <p>The hardcoded pair stays as the fallback for a caller whose list has not arrived. <b>It does not agree
 * with the channel for `bar`</b>, and this paragraph used to say it did (*"both `restaurant` and `bar` have
 * `usesTables`"*). *Osobný odber* has no tables on any of the owner's franchises, so with the list loaded this
 * answers `false` for a pickup and with the list absent `true`.
 *
 * <p><b>The list is required - §9.652, FB-981.</b> It was optional, and `PaymentDialog` called this twice
 * without it: a terrace table was never asked who took the money, and `guardClosable` then refused the close
 * with no control on screen to fix it. A caller whose read has not answered passes an empty list and says so.
 *
 * <p><b>The till no longer asks this - §9.736, owner O106, FB-1042.</b> §9.652 passed the list on the false
 * premise above, so from 2026-10-05 the payment dialog drew no question on a pickup and closed it to nobody. The
 * till asks {@link paymentAsksWhoTookTheMoney} now, and this stays what it always was: the mirror of
 * `guardClosable`'s refusal, which {@link needsWaiterBeforeClosing} reads for the archive's picker. The server
 * does not refuse a pickup nobody owns - it credits whoever closes it (`BACKEND.md` §10A.386) - so widening this
 * to `bar` would make the archive demand a waiter the server never asks for.
 */
export function paymentNeedsWaiter(
  orderType: string | undefined,
  channels: readonly { slug?: string; usesTables?: boolean }[],
): boolean {
  const known = channels.find((channel) => channel.slug === orderType)
  if (known !== undefined) return known.usesTables === true
  return orderType === 'restaurant' || orderType === 'bar'
}

/**
 * Whether the till asks who took the money - `FRONTEND.md` §9.736, owner O106, FB-1042, on `BACKEND.md`
 * §10A.386.
 *
 * <p><b>The owner:</b> *"Osobný odber - when čašníčka is closing that objednáavka, its not assigned to anyone
 * (It should be assigned to one, who is doing the final stav to "Doručená")"*. The till had stopped asking on a
 * pickup because §9.652 moved it onto {@link paymentNeedsWaiter}, whose premise that `bar` has tables is false.
 *
 * <p><b>A table, or a channel handed over in person that is paid at the hand-over</b> - on the premises, no
 * tables, not settled when it is rung up. On the owner's rows that is `bar`, *Osobný odber*. `do_ruky` settles at
 * once, so its dialog does not ask, exactly as before 2026-10-05; a delivery is its driver's and is not asked
 * either. Read off the channel rather than the slug, so a counter channel under another name is asked too.
 *
 * <p><b>Its own rule rather than a wider {@link paymentNeedsWaiter}</b>, because that one is the server's mirror:
 * {@link needsWaiterBeforeClosing} reads it, and the server credits whoever closes a pickup rather than refusing
 * it (§10A.386). The till is asked a different question - who took the money - and it is a superset of the
 * mirror: every channel the server refuses to close without a waiter is asked here, or the close is refused with
 * no control on screen to answer it (§9.652's own defect).
 *
 * <p>With the channels not loaded, the slug fallback keeps the pair, which asks on a pickup as legacy has since
 * its own fix of 2026-04-03.
 */
export function paymentAsksWhoTookTheMoney(
  orderType: string | undefined,
  channels: readonly {
    slug?: string
    usesTables?: boolean
    onPremise?: boolean
    settlesImmediately?: boolean
  }[],
): boolean {
  const known = channels.find((channel) => channel.slug === orderType)
  if (known === undefined) return orderType === 'restaurant' || orderType === 'bar'
  if (known.usesTables === true) return true
  // Handed over in person - on the premises, and no tables, which the line above has settled - and paid at the
  // hand-over rather than when it was rung up.
  return known.onPremise === true && known.settlesImmediately !== true
}

/**
 * Whether the till may start with the waitress standing at it - `FRONTEND.md` §9.736, owner O106, and §9.128 for
 * where it may not.
 *
 * <p><b>A pickup only</b>: the half of {@link paymentAsksWhoTookTheMoney} without tables - on the premises, no
 * tables, paid at the hand-over. O106 asked for *Osobný odber*: whoever closes it owns it. <b>A table keeps owner
 * point 13</b> (§9.128) - *"na jednej kase pracujú dvaja"*, two people work one till, so a colleague's table is
 * never started on whoever happens to be at the screen: it is offered its own waiter, or nobody, exactly as before.
 *
 * <p>With the channels not loaded, the slug fallback answers for `bar` alone, the pickup half of the pair.
 */
export function paymentStartsWithTheCloser(
  orderType: string | undefined,
  channels: readonly {
    slug?: string
    usesTables?: boolean
    onPremise?: boolean
    settlesImmediately?: boolean
  }[],
): boolean {
  const known = channels.find((channel) => channel.slug === orderType)
  if (known === undefined) return orderType === 'bar'
  return known.usesTables !== true && known.onPremise === true && known.settlesImmediately !== true
}

/**
 * The waiter the till starts with - §9.320, and §9.736 for the rest.
 *
 * - **The order's own**, when she is on today's shift list (§9.320, unchanged). Naming her keeps the order with
 *   the person it already belongs to.
 * - **Otherwise, on a pickup, the signed-in waitress** when she is on it ({@link initialWaiterId}, which the
 *   order builder already uses; {@link paymentStartsWithTheCloser} says where). Owner O106: a pickup belongs to
 *   whoever hands it over, so a waitress closing one nobody owns - or one a rozvozár is on - pays with herself
 *   already chosen, and the server then drops the rozvozár unless he carried it (`BACKEND.md` §10A.386).
 *   Somebody else at a shared till changes the select, which is why it is a question with an answer drawn
 *   rather than a default (§10A.68). **Never on a table** - owner point 13 (§9.128).
 * - **Otherwise nobody**, and the question stays open.
 *
 * <p><b>Only on a pickup the till asks about</b>, because she would move the money. A dialog that draws no
 * question still sends what is chosen, and its shift list can be a cached answer from a table rung up earlier -
 * so without the gate a *Do ruky* sale paid in the dialog after the builder rang it up would be handed to her by
 * an answer nobody saw. The order's own waiter needs no such gate, because naming her keeps the order where it
 * is.
 */
export function initialPayWaiterId(
  assigned: number | null | undefined,
  startsWithTheCloser: boolean,
  user: { id?: number; systemRole?: string } | null | undefined,
  waiters: readonly WaiterOption[],
): number | null {
  if (typeof assigned === 'number' && waiters.some((waiter) => waiter.id === assigned)) return assigned
  return startsWithTheCloser ? initialWaiterId(user, waiters) : null
}

/**
 * Whether this order cannot be closed until somebody owns it - §9.333.
 *
 * The three conditions the server's own guard uses, in the same order: the order is still open, its
 * channel takes tables, and nobody is on it. All three, because a picker on a closed order or on one
 * that already has a waiter is a fourth place to set a waiter on a row where the question does not
 * arise.
 */
export function needsWaiterBeforeClosing(
  order: { status?: string; type?: string; employeeId?: number },
  // §9.652 - required, like the rule it delegates to. Its one caller already passes the list.
  channels: readonly { slug?: string; usesTables?: boolean }[],
): boolean {
  if (order.status === 'delivered' || order.status === 'cancelled') return false
  if (!paymentNeedsWaiter(order.type, channels)) return false
  return order.employeeId === undefined || order.employeeId === null
}
